import { Box, Flex, Text, Badge, SimpleGrid } from "@chakra-ui/react";
import { AppButton, AppModal } from "../../components";
import { colors } from "../../theme";
import { useAdminOrder } from "../../services/useQueries";
import { formatDateTime, formatOMR, orderLabel, ORDER_STATUS, PAYMENT_METHOD, paymentState } from "../../utils/format";

const Section = ({ title, children }) => (
  <Box>
    <Text fontSize="xs" fontWeight="700" color={colors.textSecondary} textTransform="uppercase" letterSpacing="wide" mb={2}>
      {title}
    </Text>
    {children}
  </Box>
);

const Line = ({ children, muted }) => (
  <Text fontSize="sm" color={muted ? colors.textSecondary : colors.text} wordBreak="break-word">
    {children}
  </Text>
);

const Row = ({ label, value, strong, color }) => (
  <Flex justify="space-between" gap={4} py={1}>
    <Text fontSize="sm" color={color || colors.textSecondary} fontWeight={strong ? "700" : "400"}>
      {label}
    </Text>
    <Text fontSize="sm" color={color || colors.text} fontWeight={strong ? "700" : "400"} whiteSpace="nowrap">
      {value}
    </Text>
  </Flex>
);

/**
 * One order, for support: what was ordered (with the chosen options), where it
 * goes, both sides' contact details, and payment. Read-only.
 */
const OrderDetailsModal = ({ orderId, onClose }) => {
  const { data, isLoading, isError } = useAdminOrder(orderId);
  const order = data?.data;
  const status = ORDER_STATUS[order?.status] || { label: order?.status, palette: "gray" };

  return (
    <AppModal
      isOpen={!!orderId}
      onClose={onClose}
      title={order ? `Order ${orderLabel(order.orderNumber)}` : "Order"}
      size="lg"
      footer={
        <Flex justify="flex-end" w="100%">
          <AppButton variant="secondary" onClick={onClose}>
            Close
          </AppButton>
        </Flex>
      }
    >
      {isLoading ? (
        <Text color={colors.textSecondary}>Loading…</Text>
      ) : isError || !order ? (
        <Text color={colors.textSecondary}>Couldn't load this order. Close and try again.</Text>
      ) : (
        <Flex direction="column" gap={6}>
          <Flex gap={3} align="center" wrap="wrap">
            <Badge colorPalette={status.palette} px={2} py={1} borderRadius="full" fontSize="xs">
              {status.label}
            </Badge>
            <Text fontSize="sm" color={colors.textSecondary}>
              Placed {formatDateTime(order.createdAt)}
              {order.deliveredAt ? ` · delivered ${formatDateTime(order.deliveredAt)}` : ""}
            </Text>
          </Flex>

          <Section title="Items">
            {order.items.map((item) => (
              <Flex
                key={item.id}
                justify="space-between"
                gap={4}
                py={2}
                borderBottom="1px solid"
                borderColor={colors.disabled}
              >
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="600" color={colors.text}>
                    {item.quantity} × {item.itemName}
                  </Text>
                  {item.options?.map((line) => (
                    <Line key={line} muted>
                      {line}
                    </Line>
                  ))}
                  {item.specialNotes && <Line muted>Note: {item.specialNotes}</Line>}
                </Box>
                <Text fontSize="sm" color={colors.text} whiteSpace="nowrap">
                  {formatOMR(item.totalPrice)}
                </Text>
              </Flex>
            ))}
            <Box pt={2}>
              <Row label="Subtotal" value={formatOMR(order.pricing.subtotal)} />
              {order.pricing.discount > 0 && (
                <Row
                  label={order.promoCode ? `Discount (${order.promoCode})` : "Discount"}
                  value={`-${formatOMR(order.pricing.discount)}`}
                  color="#15803D"
                />
              )}
              {order.pricing.deliveryFee > 0 && <Row label="Delivery" value={formatOMR(order.pricing.deliveryFee)} />}
              <Row label="Total" value={formatOMR(order.pricing.total)} strong />
            </Box>
          </Section>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
            <Section title="Deliver to">
              {order.deliveryAddress?.label && <Line>{order.deliveryAddress.label}</Line>}
              <Line muted>{order.deliveryAddress?.address || "—"}</Line>
              {order.specialInstructions && (
                <Box mt={2}>
                  <Line>Note for the shop</Line>
                  <Line muted>{order.specialInstructions}</Line>
                </Box>
              )}
            </Section>

            <Section title="Payment">
              <Line>{PAYMENT_METHOD[order.paymentMethod] || order.paymentMethod}</Line>
              <Line muted>
                {paymentState(order)}
                {order.paidAt ? `, ${formatDateTime(order.paidAt)}` : ""}
              </Line>
            </Section>

            <Section title="Customer">
              <Line>{order.user?.name || "Customer"}</Line>
              <Line muted>{order.user?.phone || "No phone number"}</Line>
              <Line muted>{order.user?.email || ""}</Line>
            </Section>

            <Section title="Shop">
              <Line>{order.restaurant?.name}</Line>
              <Line muted>{order.restaurant?.mall?.name}</Line>
              <Line muted>{order.shopContact?.phone || "No phone number"}</Line>
              <Line muted>{order.shopContact?.email || ""}</Line>
            </Section>
          </SimpleGrid>
        </Flex>
      )}
    </AppModal>
  );
};

export default OrderDetailsModal;
